import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  makeStyles,
  Tab,
  TabList,
} from '@fluentui/react-components';
import { AddRegular, Dismiss24Regular } from '@fluentui/react-icons';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { TDocument } from '@modules/documents';
import { TFile } from '@modules/files';
import { SignInContext } from '@modules/users';
import { useSearchParams } from '@shared/hooks';
import { localDateToYMD } from '@shared/utils';
import { PERIODIC_REVIEW_QUERY_PARAM } from '../../constants';
import { useUpdatePeriodicReview } from '../../api/periodicReviews.api';
import { useCanEdit } from '@modules/access/hooks/useCanEdit.hook';
import { AccessType, type TPermissions } from '@modules/access/types';
import { EPeriodicReviewIntervalMonths } from '../../types/periodicReviews.types';
import type {
  TPeriodicReview,
  TPeriodicReviewItemType,
  TPeriodicReviewSavingField,
  TPeriodicReviewSettingsValues,
  TPeriodicReviewUpdate,
} from '../../types/periodicReviews.types';
import CompletePeriodicReviewDialog from './CompletePeriodicReviewDialog';
import PeriodicReviewHistoryTable from './PeriodicReviewHistoryTable';
import PeriodicReviewSettings from './PeriodicReviewSettings';
import { dateToYMD } from '@shared/utils/date';

type TProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  item: TDocument | TFile;
  itemType: TPeriodicReviewItemType;
  permissions: TPermissions;
};

const PERIODIC_REVIEW_DISABLED_MESSAGE = 'Periodic review is disabled.';

type TPeriodicReviewTab = 'settings' | 'history';

const toSettingsValues = (periodicReview?: TPeriodicReview): TPeriodicReviewSettingsValues => ({
  reviewIntervalMonths: periodicReview?.reviewIntervalMonths ?? EPeriodicReviewIntervalMonths.TWELVE,
  nextReviewDate: dateToYMD(periodicReview?.nextReviewDate),
  responsibleUser: periodicReview?.responsibleUser ?? '',
});

const useStyles = makeStyles({
  surface: {
    maxWidth: '720px',
    width: '100%',
  },
  surfaceWide: {
    maxWidth: '960px',
    width: 'calc(100vw - 48px)',
  },
});

const PeriodicReviewDialog = ({ isOpen, setIsOpen, item, itemType, permissions }: TProps) => {
  const { user } = useContext(SignInContext);
  const { changeMany } = useSearchParams();
  const updatePeriodicReview = useUpdatePeriodicReview();
  const { canEdit } = useCanEdit(permissions.accessRight);
  const periodicReview = item.periodicReview;
  const approvedVersionId = item.stats.approvedVersionId;
  const canComplete = Boolean(
    periodicReview?.enabled &&
      !item.isFrozen &&
      (permissions.accessRight >= AccessType.EDIT || periodicReview.responsibleUser === user?._id)
  );
  const [selectedTab, setSelectedTab] = useState<TPeriodicReviewTab>('settings');
  const [values, setValues] = useState<TPeriodicReviewSettingsValues>(() => toSettingsValues(periodicReview));
  const originalValues = useRef(values);
  const [savingField, setSavingField] = useState<TPeriodicReviewSavingField>(null);
  const [isCompleteOpen, setIsCompleteOpen] = useState(false);
  const isSaving = savingField !== null;

  const syncValues = useCallback((nextPeriodicReview?: TPeriodicReview) => {
    const nextValues = toSettingsValues(nextPeriodicReview);
    setValues(nextValues);
    originalValues.current = nextValues;
  }, []);

  useEffect(() => {
    syncValues(periodicReview);
  }, [periodicReview, syncValues]);

  useEffect(() => {
    if (isOpen && periodicReview && !periodicReview.enabled && periodicReview.history.length > 0)
      setSelectedTab('history');
  }, [isOpen, periodicReview]);

  const save = async (
    field: Exclude<TPeriodicReviewSavingField, null>,
    nextValues: TPeriodicReviewSettingsValues,
    update: TPeriodicReviewUpdate
  ) => {
    setValues(nextValues);
    setSavingField(field);
    try {
      const nextPeriodicReview = await updatePeriodicReview.mutateAsync({
        id: item._id,
        itemType,
        update,
      });
      syncValues(nextPeriodicReview);
    } catch {
      setValues(originalValues.current);
    } finally {
      setSavingField(null);
    }
  };

  const handleIntervalChange = async (selectedValue?: string) => {
    if (!selectedValue) return;
    const nextInterval = Number(selectedValue) as EPeriodicReviewIntervalMonths;
    if (nextInterval === originalValues.current.reviewIntervalMonths) return;

    // The backend shifts nextReviewDate by the interval difference; syncValues applies the result.
    await save(
      'reviewIntervalMonths',
      { ...values, reviewIntervalMonths: nextInterval },
      {
        reviewIntervalMonths: nextInterval,
      }
    );
  };

  const handleDateChange = async (date?: Date | null) => {
    const nextReviewDate = date ? localDateToYMD(date) : '';
    if (!nextReviewDate || nextReviewDate === originalValues.current.nextReviewDate) return;

    const nextValues = { ...values, nextReviewDate };
    await save('nextReviewDate', nextValues, { nextReviewDate });
  };

  const handleResponsibleUserChange = async (responsibleUser: string | null) => {
    if (!responsibleUser || responsibleUser === originalValues.current.responsibleUser) return;

    const nextValues = { ...values, responsibleUser };
    await save('responsibleUser', nextValues, { responsibleUser });
  };

  const handleViewVersion = (versionId: string) => {
    if (!versionId) return;
    setIsOpen(false);
    changeMany({
      view: versionId,
      [PERIODIC_REVIEW_QUERY_PARAM]: undefined,
    });
  };

  const classes = useStyles();

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(_, data) => setIsOpen(data.open)}>
        <DialogSurface className={selectedTab === 'history' ? classes.surfaceWide : classes.surface}>
          <DialogBody>
            <DialogTitle
              action={
                <DialogTrigger action="close">
                  <Button appearance="subtle" aria-label="close" icon={<Dismiss24Regular />} />
                </DialogTrigger>
              }
            >
              Periodic review
            </DialogTitle>
            <DialogContent>
              {!periodicReview ? (
                <p className="text-neutral-500">{PERIODIC_REVIEW_DISABLED_MESSAGE}</p>
              ) : (
                <div className="flex flex-col gap-4">
                  <TabList
                    selectedValue={selectedTab}
                    onTabSelect={(_, data) => setSelectedTab(data.value as TPeriodicReviewTab)}
                    size="small"
                  >
                    <Tab value="settings">Settings</Tab>
                    <Tab value="history">History</Tab>
                  </TabList>

                  {selectedTab === 'settings' ? (
                    periodicReview.enabled ? (
                      <PeriodicReviewSettings
                        values={values}
                        canEdit={canEdit}
                        canComplete={canComplete}
                        approvedVersionId={approvedVersionId}
                        savingField={savingField}
                        onIntervalChange={handleIntervalChange}
                        onDateChange={handleDateChange}
                        onResponsibleUserChange={handleResponsibleUserChange}
                        onViewVersion={handleViewVersion}
                      />
                    ) : (
                      <p className="text-neutral-500">{PERIODIC_REVIEW_DISABLED_MESSAGE}</p>
                    )
                  ) : (
                    <PeriodicReviewHistoryTable history={periodicReview.history} onViewVersion={handleViewVersion} />
                  )}
                </div>
              )}
            </DialogContent>
            {periodicReview?.enabled && canComplete && (
              <DialogActions>
                <Button
                  appearance="primary"
                  icon={<AddRegular />}
                  disabled={!approvedVersionId || isSaving}
                  onClick={() => setIsCompleteOpen(true)}
                >
                  New review
                </Button>
              </DialogActions>
            )}
          </DialogBody>
        </DialogSurface>
      </Dialog>
      <CompletePeriodicReviewDialog
        isOpen={isCompleteOpen}
        setIsOpen={setIsCompleteOpen}
        item={item}
        itemType={itemType}
        onCompleted={() => setSelectedTab('history')}
      />
    </>
  );
};

export default PeriodicReviewDialog;
